import { ORDER_FLOW, type OrderStatus, TRANSITION_ACTOR } from '@ambernord/shared';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Modal,
  Select,
  Skeleton,
  SkeletonRows,
  StatusBadge,
  useToast,
} from '@ambernord/ui';
import { AlertTriangle, ArrowLeft, CheckCircle2, PackageSearch } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { AppLayout } from '../components/AppLayout.js';
import { api } from '../lib/api.js';
import { formatDate, formatDateTime, formatNumber, productName } from '../lib/format.js';
import { ArtifactChecklist } from './supply-order/ArtifactChecklist.js';
import { CarrierPanel } from './supply-order/CarrierPanel.js';
import { ClaimPanel } from './supply-order/ClaimPanel.js';
import { LifecycleStepper } from './supply-order/LifecycleStepper.js';
import { PackingPanel } from './supply-order/PackingPanel.js';
import {
  type ArtifactsDto,
  type PackingDto,
  type ShippingDocItem,
  type SupplyOrderDto,
  actorKindOf,
} from './supply-order/types.js';

interface ProductLite {
  id: string;
  sku?: string;
  namesI18n?: Record<string, string> | null;
}

interface LoadingAddr {
  id: string;
  label: string;
  city: string;
  country: string;
}

/** Nākamais uz-priekšu statuss (izlaižot `cancelled`). */
function nextForward(status: OrderStatus): OrderStatus | undefined {
  return ORDER_FLOW[status].find((s) => s !== 'cancelled');
}

/**
 * Piegādes pasūtījuma detaļu lapa — dzīves cikla flagmanis.
 * §7 KRITISKI: piegādātāja sesijā GET /:id atgriež anonimizēto projekciju; lapa
 * renderē TIEŠI API atbildi un NEKAD nepieprasa /api/orders no piegādātāja sesijas.
 */
export default function SupplyOrderDetail() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation(['sodetail', 'statuses']);
  const lang = i18n.language;
  const { toast } = useToast();
  const { user } = useAuth();
  const role = user?.role ?? '';
  const mine = actorKindOf(role);
  const isSupplierRole = mine === 'supplier';
  // Piegādātājs un pārvadātājs saņem vienu anonimizēto projekciju (§7) — pircējs vienmēr platforma.
  const isAnonymizedRole = isSupplierRole || mine === 'carrier';
  const canEditDocs = mine === 'platform' || mine === 'supplier';

  const [so, setSo] = useState<SupplyOrderDto | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [artifacts, setArtifacts] = useState<ArtifactsDto | null>(null);
  const [docs, setDocs] = useState<ShippingDocItem[]>([]);
  const [packing, setPacking] = useState<PackingDto | null>(null);
  const [products, setProducts] = useState<ProductLite[]>([]);
  const [addresses, setAddresses] = useState<LoadingAddr[]>([]);

  const [busy, setBusy] = useState(false);
  const [blockedMissing, setBlockedMissing] = useState<string[] | null>(null);

  // Piegādātāja apstiprināšanas modālis (datums + iekraušanas adrese).
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDate, setConfirmDate] = useState('');
  const [confirmAddr, setConfirmAddr] = useState('');
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await api.get<{ supplyOrder?: SupplyOrderDto; error?: string }>(
      `/api/supply-orders/${id}`,
    );
    if (res.status === 404) {
      setNotFound(true);
      return;
    }
    if (!res.ok || !res.data.supplyOrder) {
      setLoadError(true);
      return;
    }
    setNotFound(false);
    setLoadError(false);
    setSo(res.data.supplyOrder);

    const [artRes, docRes, packRes] = await Promise.all([
      api.get<ArtifactsDto>(`/api/supply-orders/${id}/artifacts`),
      api.get<{ items?: ShippingDocItem[] }>(`/api/shipping-docs/supply-order/${id}`),
      api.get<PackingDto & { error?: string }>(`/api/supply-orders/${id}/packing`),
    ]);
    if (artRes.ok) setArtifacts(artRes.data);
    if (docRes.ok) setDocs(docRes.data.items ?? []);
    if (packRes.ok)
      setPacking({ shipment: packRes.data.shipment, packages: packRes.data.packages ?? [] });
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Produktu vārdnīca (produkta nosaukumam) — pieejama visām lomām.
  useEffect(() => {
    void (async () => {
      const res = await api.get<{ items?: ProductLite[] }>('/api/products');
      if (res.ok) setProducts(res.data.items ?? []);
    })();
  }, []);

  // Iekraušanas adreses apstiprināšanas formai — tikai piegādātājam, kad tas aktuāli.
  useEffect(() => {
    if (!isSupplierRole || so?.status !== 'awaiting_confirmation') return;
    void (async () => {
      const res = await api.get<{ items?: LoadingAddr[] }>('/api/registry/loading-addresses');
      if (res.ok) setAddresses(res.data.items ?? []);
    })();
  }, [isSupplierRole, so?.status]);

  const product = useMemo(
    () => products.find((p) => p.id === so?.terms?.productId),
    [products, so],
  );

  const next = so ? nextForward(so.status) : undefined;
  const myTurn = next !== undefined && TRANSITION_ACTOR[next] === mine;

  async function advance() {
    if (!so || !next) return;
    setBusy(true);
    setBlockedMissing(null);
    try {
      const res =
        next === 'paid'
          ? await api.post<{ error?: string; details?: { missing?: string[] } }>(
              `/api/supply-orders/${so.id}/mark-paid`,
            )
          : await api.post<{ error?: string; details?: { missing?: string[] } }>(
              `/api/supply-orders/${so.id}/advance`,
              { toStatus: next },
            );
      if (res.ok) {
        toast(t('action.success'));
        await load();
      } else if (res.status === 409 && res.data.error === 'artifact_required') {
        setBlockedMissing(res.data.details?.missing ?? []);
        toast(t('action.error'), 'error');
      } else {
        toast(res.data.error ?? t('action.error'), 'error');
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitConfirm() {
    if (!so) return;
    setConfirmError(null);
    if (!confirmDate || !confirmAddr) {
      setConfirmError(t('confirm.missing'));
      return;
    }
    setBusy(true);
    try {
      const res = await api.post<{ error?: string }>(`/api/supply-orders/${so.id}/confirm`, {
        confirmedDeliveryDate: new Date(confirmDate).toISOString(),
        loadingAddressId: confirmAddr,
      });
      if (res.ok) {
        toast(t('confirm.success'));
        setConfirmOpen(false);
        await load();
      } else {
        toast(res.data.error ?? t('action.error'), 'error');
      }
    } finally {
      setBusy(false);
    }
  }

  function addressLine(a: SupplyOrderDto['shipToProjection']): string {
    if (!a) return '—';
    return (
      [a.line1, [a.postalCode, a.city].filter(Boolean).join(' '), a.country]
        .filter(Boolean)
        .join(', ') || '—'
    );
  }

  // --- Ielādes skelets / nav atrasts / kļūda ---
  if (notFound) {
    return (
      <AppLayout title={t('title')}>
        <EmptyState
          icon={<PackageSearch className="h-6 w-6" aria-hidden />}
          title={t('notFound.title')}
          hint={t('notFound.hint')}
          action={
            <Link to="/supply-orders">
              <Button variant="secondary">
                <ArrowLeft className="h-4 w-4" aria-hidden />
                {t('back')}
              </Button>
            </Link>
          }
        />
      </AppLayout>
    );
  }

  if (loadError) {
    return (
      <AppLayout title={t('title')}>
        <p className="text-sm text-red-600 dark:text-red-400" role="alert">
          {t('error')}
        </p>
      </AppLayout>
    );
  }

  if (!so) {
    return (
      <AppLayout title={t('title')}>
        <div className="space-y-4" aria-busy="true">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-24 w-full" />
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="space-y-4 lg:col-span-2">
              <SkeletonRows rows={5} />
            </div>
            <SkeletonRows rows={4} />
          </div>
        </div>
      </AppLayout>
    );
  }

  const qty = so.terms?.qty;
  // Pārvadātāja panelis: platforma norīko, pārvadātājs apstiprina, piegādātājs lasa nosaukumu.
  const showCarrier = mine === 'platform' || mine === 'supplier' || mine === 'carrier';
  const showClaim =
    (mine === 'platform' || mine === 'customer') &&
    (so.status === 'delivered' || so.status === 'inspected');

  return (
    <AppLayout title={so.ref}>
      <Link
        to="/supply-orders"
        className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        {t('back')}
      </Link>

      {/* Galvene: ref + statuss + produkts/daudzums + režīms */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <StatusBadge
          data-testid="so-status"
          status={so.status}
          label={t(`statuses:${so.status}`)}
          className="px-3 py-1 text-sm"
        />
        <span className="text-sm font-semibold text-slate-900 dark:text-white">
          {productName(product?.namesI18n, lang, so.terms?.productId ?? '—')}
        </span>
        {qty != null ? (
          <span className="text-sm text-slate-500 dark:text-slate-400">
            {formatNumber(qty, lang)} {so.terms?.unit ?? 'kg'}
          </span>
        ) : null}
        {so.shippingMode ? (
          <Badge tone={so.shippingMode === 'direct' ? 'blue' : 'violet'}>
            {t(`header.mode.${so.shippingMode}`)}
          </Badge>
        ) : null}
        {isAnonymizedRole ? (
          <span className="text-xs text-slate-400 dark:text-slate-500">
            {t('header.buyer')}: {t('header.buyerPlatform')}
          </span>
        ) : null}
      </div>

      {/* Atcelts → sarkans baneris steppera vietā */}
      {so.status === 'cancelled' ? (
        <div
          className="mb-5 flex items-start gap-3 rounded-xl border border-red-300 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/40"
          role="alert"
        >
          <AlertTriangle
            className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400"
            aria-hidden
          />
          <div>
            <p className="text-sm font-semibold text-red-800 dark:text-red-300">
              {t('cancelled.title')}
            </p>
            <p className="text-xs text-red-700 dark:text-red-400">{t('cancelled.hint')}</p>
          </div>
        </div>
      ) : (
        <Card className="mb-5">
          <CardHeader title={t('timeline.title')} />
          <CardBody>
            <LifecycleStepper so={so} />
          </CardBody>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Darbība + pasūtījuma dati */}
        <div className="space-y-4 lg:order-2">
          {so.status !== 'cancelled' ? (
            <Card>
              <CardHeader title={t('action.title')} />
              <CardBody className="space-y-3">
                {so.status === 'paid' ? (
                  <p className="flex items-center gap-2 text-sm font-medium text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="h-4 w-4" aria-hidden />
                    {t('action.completed')}
                  </p>
                ) : next && myTurn ? (
                  so.status === 'awaiting_confirmation' && mine === 'supplier' ? (
                    <Button className="w-full" disabled={busy} onClick={() => setConfirmOpen(true)}>
                      {t('action.confirm')}
                    </Button>
                  ) : (
                    <Button className="w-full" disabled={busy} onClick={() => void advance()}>
                      {next === 'paid'
                        ? t('action.markPaid')
                        : t('action.advance', { status: t(`statuses:${next}`) })}
                    </Button>
                  )
                ) : next ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t('action.waiting', {
                      actor: t(`sodetail:actors.${TRANSITION_ACTOR[next]}`),
                    })}
                  </p>
                ) : null}

                {blockedMissing && blockedMissing.length > 0 ? (
                  <div
                    className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                    role="alert"
                  >
                    <p className="mb-1 font-semibold">{t('action.blocked')}</p>
                    <ul className="mb-2 list-inside list-disc">
                      {blockedMissing.map((k) => (
                        <li key={k}>{t(`artifacts.kinds.${k}`)}</li>
                      ))}
                    </ul>
                    <a href="#artifacts" className="font-medium underline">
                      {t('action.toChecklist')}
                    </a>
                  </div>
                ) : null}
              </CardBody>
            </Card>
          ) : null}

          {showCarrier ? <CarrierPanel so={so} mine={mine} onChanged={load} /> : null}

          <Card>
            <CardHeader title={t('info.title')} />
            <CardBody>
              <dl className="space-y-3">
                {[
                  [
                    t('info.deliveryWindow'),
                    so.deliveryWindowStart || so.deliveryWindowEnd
                      ? `${formatDate(so.deliveryWindowStart, lang)} – ${formatDate(so.deliveryWindowEnd, lang)}`
                      : '—',
                  ],
                  [t('info.confirmationDeadline'), formatDateTime(so.confirmationDeadline, lang)],
                  [t('info.confirmedDelivery'), formatDate(so.confirmedDeliveryDate, lang)],
                  [t('info.shipTo'), addressLine(so.shipToProjection)],
                  [
                    t('info.consignee'),
                    so.consignee
                      ? [so.consignee.name, so.consignee.city].filter(Boolean).join(', ') || '—'
                      : '—',
                  ],
                  [
                    t('info.route'),
                    so.routeOrigin || so.routeDestination
                      ? `${so.routeOrigin ?? '—'} → ${so.routeDestination ?? '—'}`
                      : '—',
                  ],
                  [t('info.notes'), so.terms?.notes ?? '—'],
                ].map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-xs text-slate-500 dark:text-slate-400">{label}</dt>
                    <dd className="text-sm font-medium text-slate-900 dark:text-white">{value}</dd>
                  </div>
                ))}
              </dl>
            </CardBody>
          </Card>
        </div>

        {/* Dokumenti + iepakojums + inspekcija */}
        <div className="space-y-4 lg:order-1 lg:col-span-2">
          <ArtifactChecklist
            supplyOrderId={so.id}
            artifacts={artifacts}
            docs={docs}
            canGenerate={canEditDocs}
            canUpload={canEditDocs}
            onChanged={load}
          />
          <PackingPanel
            supplyOrderId={so.id}
            packing={packing}
            canEdit={canEditDocs}
            onChanged={load}
          />
          {showClaim ? <ClaimPanel so={so} onChanged={load} /> : null}
        </div>
      </div>

      {/* Piegādātāja apstiprināšanas modālis */}
      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} title={t('confirm.title')}>
        <div className="space-y-3">
          <Field label={t('confirm.date')} htmlFor="confirm-date">
            <Input
              id="confirm-date"
              type="datetime-local"
              value={confirmDate}
              onChange={(e) => setConfirmDate(e.target.value)}
            />
          </Field>
          <Field label={t('confirm.address')} htmlFor="confirm-addr" error={confirmError}>
            <Select
              id="confirm-addr"
              value={confirmAddr}
              onChange={(e) => setConfirmAddr(e.target.value)}
            >
              <option value="">{t('confirm.addressPlaceholder')}</option>
              {addresses.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label} · {a.city}
                </option>
              ))}
            </Select>
          </Field>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              {t('confirm.cancel')}
            </Button>
            <Button disabled={busy} onClick={() => void submitConfirm()}>
              {t('confirm.submit')}
            </Button>
          </div>
        </div>
      </Modal>
    </AppLayout>
  );
}
